import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./MissingCustomQuestions.module.scss";
import { DIRECT_ACCESS_URL } from "../../constants/constants";

type CustomQuestion = {
  id: number;
  question_id: number;
  question?: string;
  label?: string;
  input_type?: number;
  inpt_type?: number;
  is_required: number;
  answer: string;
  options?: Array<{ title: string; value: any; id?: number }>;
  bundle_instance_id?: string | null;
};

type CustomQuestionForm = {
  bundle_instance_id: string | null;
  label: string;
  questions: CustomQuestion[];
};

type BundleGroup = {
  label: string;
  bundle_instance_id: string | null;
  player_first_name?: string | null;
  player_last_name?: string | null;
  products: Array<{
    id: number;
    product_name: string;
    quantity: number;
    order_product_options?: Array<{
      product_option_value?: string;
      display_name?: string | null;
    }>;
  }>;
};

type OrderPayload = {
  order_id: number;
  customer_order_id: string;
  email: string;
  store_id: number;
  store_name?: string;
  player_first_name?: string;
  player_last_name?: string;
  phone_number?: string;
  created_at?: string;
  createdAt?: string;
  is_multi_bundle: boolean;
  already_submitted: boolean;
  bundle_groups: BundleGroup[];
  custom_question_forms: CustomQuestionForm[];
};

const MissingCustomQuestions = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [orderData, setOrderData] = useState<OrderPayload | null>(null);
  const [forms, setForms] = useState<CustomQuestionForm[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState(false);
  const [alreadyUpdated, setAlreadyUpdated] = useState(false);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setErrorMessage("");
      try {
        const token = searchParams.get("token");
        if (!token) {
          navigate("/page-not-found", { replace: true });
          return;
        }

        // Ensure this store subdomain exists (store-specific page)
        const storeRes = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/get-store-info/${DIRECT_ACCESS_URL}`
        );
        const storeInfo = storeRes?.data?.data;
        if (!storeInfo?.id) {
          navigate("/store-not-found", { replace: true });
          return;
        }

        const orderRes = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/order/custom-questions-by-token`,
          {
            headers: { "x-auth": token },
            params: { token },
          }
        );
        const payload = orderRes?.data?.data as OrderPayload;
        if (!payload) {
          navigate("/page-not-found", { replace: true });
          return;
        }

        // Token order must belong to this store subdomain
        if (Number(payload.store_id) !== Number(storeInfo.id)) {
          navigate("/page-not-found", { replace: true });
          return;
        }

        setOrderData(payload);
        setForms(payload.custom_question_forms || []);
        setAlreadyUpdated(Boolean(payload.already_submitted));
        if (payload.already_submitted) {
          setIsSubmittedSuccessfully(true);
        }
      } catch (error: any) {
        if (
          error?.response?.status === 401 ||
          error?.response?.status === 404
        ) {
          navigate("/page-not-found", { replace: true });
          return;
        }
        setErrorMessage("Unable to load order details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [searchParams, navigate]);

  const hasQuestions = useMemo(
    () => forms.some((form) => (form.questions || []).length > 0),
    [forms]
  );

  const handleAnswerChange = (
    formIndex: number,
    questionId: number,
    value: string
  ) => {
    const errorKey = `${formIndex}_${questionId}`;
    setFieldErrors((prev) => {
      if (!prev[errorKey]) return prev;
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });

    setForms((prev) =>
      prev.map((form, idx) => {
        if (idx !== formIndex) return form;
        return {
          ...form,
          questions: (form.questions || []).map((q) =>
            q.question_id === questionId ? { ...q, answer: value } : q
          ),
        };
      })
    );
  };

  const handleSubmit = async () => {
    if (!orderData || loading || isSubmitting || alreadyUpdated) return;
    const token = searchParams.get("token");
    if (!token) {
      toast.error("Invalid link. Missing token.");
      return;
    }

    const errors: Record<string, boolean> = {};
    const custom_question_answer: Array<{
      question_id: number;
      answer: string;
      bundle_instance_id: string | null;
    }> = [];

    forms.forEach((form, formIndex) => {
      (form.questions || []).forEach((q) => {
        const answer = String(q.answer ?? "").trim();
        if (Number(q.is_required) === 1 && !answer) {
          errors[`${formIndex}_${q.question_id}`] = true;
        }
        custom_question_answer.push({
          question_id: q.question_id,
          answer,
          bundle_instance_id: orderData.is_multi_bundle
            ? form.bundle_instance_id
            : null,
        });
      });
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error("Please answer all required questions before submitting.");
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/order/update-custom-questions-by-token`,
        {
          order_number: String(orderData.customer_order_id || ""),
          email: String(orderData.email || ""),
          store_id: orderData.store_id,
          custom_question_answer,
        },
        {
          headers: { "x-auth": token },
        }
      );
      toast.success("Your order information has been updated.");
      setAlreadyUpdated(true);
      setIsSubmittedSuccessfully(true);
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setAlreadyUpdated(true);
        setIsSubmittedSuccessfully(true);
        toast.info("Your order has already been updated.");
        return;
      }
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to save answers. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestionField = (
    formIndex: number,
    question: CustomQuestion
  ) => {
    const inputType = Number(question.inpt_type ?? question.input_type);
    const label = question.label || question.question || "Question";
    const errorKey = `${formIndex}_${question.question_id}`;
    const hasError = Boolean(fieldErrors[errorKey]);
    const value = question.answer ?? "";

    return (
      <div className={styles.fieldBlock} key={errorKey}>
        <div className={styles.fieldLabel}>
          {label}
          {Number(question.is_required) === 1 ? (
            <span className={styles.requiredMark}> *</span>
          ) : null}
        </div>

        {inputType === 1 ? (
          <select
            value={value}
            onChange={(e) =>
              handleAnswerChange(formIndex, question.question_id, e.target.value)
            }
          >
            <option value="">Select</option>
            {(question.options || []).map((opt) => (
              <option key={String(opt.id ?? opt.value)} value={opt.value}>
                {opt.title}
              </option>
            ))}
          </select>
        ) : null}

        {inputType === 2 ? (
          <input
            type="text"
            value={value}
            placeholder="Enter your detail"
            onChange={(e) =>
              handleAnswerChange(formIndex, question.question_id, e.target.value)
            }
          />
        ) : null}

        {inputType === 3 ? (
          <textarea
            rows={3}
            value={value}
            placeholder="Enter your detail"
            onChange={(e) =>
              handleAnswerChange(formIndex, question.question_id, e.target.value)
            }
          />
        ) : null}

        {inputType === 4 ? (
          <div className={styles.radioRow}>
            <label>
              <input
                type="radio"
                name={errorKey}
                value="1"
                checked={value === "1"}
                onChange={(e) =>
                  handleAnswerChange(
                    formIndex,
                    question.question_id,
                    e.target.value
                  )
                }
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                name={errorKey}
                value="0"
                checked={value === "0"}
                onChange={(e) =>
                  handleAnswerChange(
                    formIndex,
                    question.question_id,
                    e.target.value
                  )
                }
              />
              No
            </label>
          </div>
        ) : null}

        {hasError ? (
          <span className={styles.fieldError}>Please enter detail</span>
        ) : null}
      </div>
    );
  };

  return (
    <div className={styles.renWrap}>
      <div className={styles.renHeader}>
        <img
          src="/images/logo.svg"
          alt="REN Athletics"
          className={styles.headerLogo}
        />
      </div>

      <div className={styles.renBody}>
        {isSubmittedSuccessfully || alreadyUpdated ? (
          <div className={styles.successScreen}>
            <p className={styles.successOrderNumber}>
              Order Number #{orderData?.customer_order_id || "-"}
            </p>
            <h2 className={styles.successTitle}>Thank You!</h2>
            <p className={styles.successText}>
              Your order has already been updated. The custom field questions
              for this order have been saved and no further action is needed.
            </p>
          </div>
        ) : (
          <>
            <div className={styles.orderMeta}>
              <div>
                <p className={styles.orderNum}>
                  Order Number #{orderData?.customer_order_id || "-"}
                </p>
                <div className={styles.storeText}>
                  Store:{" "}
                  <strong>{orderData?.store_name || "REN Athletics"}</strong>
                </div>
              </div>
              <div className={styles.orderDateBlock}>
                Order Date
                <br />
                <strong>
                  {orderData?.created_at || orderData?.createdAt
                    ? new Date(
                        orderData.created_at || orderData.createdAt || ""
                      ).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "-"}
                </strong>
              </div>
            </div>

            <div className={styles.contactBlock}>
              <p>
                <strong>Customer</strong>
              </p>
              <p>Email: {orderData?.email || "-"}</p>
              <p>
                Player:{" "}
                {[orderData?.player_first_name, orderData?.player_last_name]
                  .filter(Boolean)
                  .join(" ") || "-"}
              </p>
            </div>

            <hr className={styles.divider} />

            <div className={styles.noticeBox}>
              <p>
                We apologize — due to a technical issue, some{" "}
                <strong>important questions</strong> related to your order
                were skipped at checkout. Please answer them below and click{" "}
                <strong>Save info</strong> so we can update and process your order correctly.
              </p>
            </div>

            {loading ? (
              <div className={styles.stateCell}>Loading order details...</div>
            ) : null}

            {!loading && errorMessage ? (
              <div className={styles.errorCell}>{errorMessage}</div>
            ) : null}

            {!loading && !errorMessage ? (
              <>
                <p className={styles.sectionTitle}>Order products</p>
                {(orderData?.bundle_groups || []).map((group, idx) => (
                  <div className={styles.bundleCard} key={`products_${idx}`}>
                    <div className={styles.bundleLabel}>
                      {group.label}
                      {group.player_first_name || group.player_last_name
                        ? ` — ${[
                            group.player_first_name,
                            group.player_last_name,
                          ]
                            .filter(Boolean)
                            .join(" ")}`
                        : ""}
                    </div>
                    {(group.products || []).map((product) => (
                      <div className={styles.productRow} key={product.id}>
                        <div>
                          <div className={styles.productName}>
                            {product.product_name}
                          </div>
                          {(product.order_product_options || []).length > 0 ? (
                            <div className={styles.optionText}>
                              {(product.order_product_options || [])
                                .map(
                                  (opt) =>
                                    opt.product_option_value ||
                                    opt.display_name ||
                                    ""
                                )
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                          ) : null}
                        </div>
                        <div>Qty: {product.quantity}</div>
                      </div>
                    ))}
                  </div>
                ))}

                <p className={styles.sectionTitle}>
                  {orderData?.is_multi_bundle
                    ? "Custom questions (answer for each bundle)"
                    : "Custom questions"}
                </p>

                {!hasQuestions ? (
                  <div className={styles.stateCell}>
                    There are no required custom questions remaining for this
                    order.
                  </div>
                ) : (
                  forms.map((form, formIndex) => (
                    <div
                      className={styles.bundleCard}
                      key={`form_${form.bundle_instance_id || formIndex}`}
                    >
                      {orderData?.is_multi_bundle ? (
                        <div className={styles.bundleLabel}>{form.label}</div>
                      ) : null}
                      <div className={styles.questionsWrap}>
                        {(form.questions || []).map((q) =>
                          renderQuestionField(formIndex, q)
                        )}
                      </div>
                    </div>
                  ))
                )}

                {hasQuestions ? (
                  <div className={styles.submitWrap}>
                    <button
                      type="button"
                      className={styles.submitBtn}
                      disabled={isSubmitting}
                      onClick={handleSubmit}
                    >
                      {isSubmitting ? "Saving..." : "Save info"}
                    </button>
                  </div>
                ) : null}
              </>
            ) : null}

            <p className={styles.footerNote}>REN Athletics</p>
          </>
        )}
      </div>
    </div>
  );
};

export default MissingCustomQuestions;
